import { useEffect, useRef, useState } from 'react';
import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { addBusinessDays, addCalendarDays, fmtDate, fmtDateTime } from '../../../lib/util';
import {
  BCID_APPROVED_CALL_REASONS,
  BCID_APPROVED_REASONS_URL,
  CUSTOM_CALL_REASON_MAX,
  isApprovedCallReason,
  ORG_TYPE_LABELS,
  PERSONAL_EMAIL_DOMAINS,
} from '../../../domain/constants';
import { createEmptyOnboarding } from '../../../domain/factories';
import type { Case } from '../../../domain/initial-cases';
import { StatusBadge } from '../../../components/StatusBadge';
import { DeadlineIndicator } from '../../../components/DeadlineIndicator';
import { getCurrentUser } from '../../../auth/useUser';
import { resolveActorName } from '../../../auth/actorNames';
import { acceptOnboarding, hasExecutedAgreement } from '../../../state/actions/onboarding';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import { RevetChangePanel } from '../../../components/RevetChangePanel';
import { COMPANY_FIELD_LABEL, formatCompanyFieldValue } from '../../../api/intake';
import { getCase } from '../../../api/cases';
import { waitForLogoSnapshot } from '../../../api/logoSnapshots';

// CTIA BCID Platform hard limits on the branded-call fields (issue #111).
// CTIA rejects a DIR whose values exceed these — nam > 35 chars,
// default_crn > 64, icn_url > 128 — and the call logo (icn) must be a
// 256×256 24-bit .bmp. The intake API enforces the same limits (routes/
// intake.ts) and settlement-delivery.ts is the fail-loud backstop; flagging
// them here means an analyst catches an unusable value at data entry instead
// of at Settlement delivery weeks later (live finding #78).
export const CTIA_ONBOARDING_LIMITS = {
  displayName: 35, // nam
  callReason: 64, // default_crn
  callLogoUrl: 128, // icn_url
} as const;

// THE ONE LIST THAT MEANS "REQUIRED" (#519).
//
// The submit gate filters on it, and `fieldRow` derives the `*` from it. A
// field is required IF AND ONLY IF its key is in here — there is nowhere else
// to say so, which is the point.
//
// There used to be two places. The `*` was a hand-typed character inside each
// label string, maintained by eye against this array, and the two had drifted:
// 18 labels carried a star, the gate checked 16. `billingPhone` and
// `billingAddress` were starred and never checked, so Submit accepted them
// empty while the form said it would not. An asterisk that the gate does not
// keep is worse than no asterisk — it is the form telling an analyst that
// something was verified.
//
// DO NOT TYPE A `*` INTO A LABEL. It will not be enforced, and
// OnboardingTab.test.tsx fails the build if you do: it reads the stars back out
// of the rendered DOM and compares them against the fields the gate actually
// names when an empty form is submitted.
//
// #583 — THE SERVER KEEPS A COPY, and #589 MECHANICALLY TIED THE TWO.
// `apps/spa` and `apps/api` are separate workspace packages with no shared
// module between them, so `VETTING_REQUIRED_FIELDS` in
// apps/api/src/routes/cases.ts cannot import this one. The second copy exists
// because this gate guards ONE door into vetting (Submit Onboarding) and the
// cases list's "+ New Case" was another, POSTing straight into
// `status: 'IN_REVIEW'` with an empty form; the API now refuses the transition
// itself, so no future caller can reopen that door.
//
// The tie is the "REQUIRED_FIELDS matches VETTING_REQUIRED_FIELDS" test in the
// #589 section of OnboardingTab.test.tsx: it reads the API source off disk,
// proves the extraction against an exact count and the exact literal, and
// compares IN ORDER, because both sides report the missing fields in list order.
// Change one, change the other in the same commit. The convention and every
// other cross-package pair are in docs/CROSS_PACKAGE_CONSTANTS.md.
//
// Exported for that pin and nothing else — the gate and `fieldRow` below are
// still the only readers.
export const REQUIRED_FIELDS: readonly string[] = [
  // #544 — `fein` is NOT here. It is one of two acceptable identifiers now, and
  // "required" cannot express that: see IDENTIFIER_FIELDS below, which the gate
  // and the `*` both read. A DUNS-only application is a legitimate one, and
  // leaving `fein` on this list would have made every one of them unsubmittable
  // at the staff desk while intake accepted it happily — the #518 stranding, in
  // the opposite direction.
  //
  // #771 — `sicCode` joined, reversing #529/#686's "optional". The intake
  // contract requires it of NEW submissions; a case promoted from an older
  // PENDING one arrives with it empty, and this is where the analyst is asked.
  'legalName', 'website', 'orgType', 'jurisdiction', 'sicCode',
  'address', 'city', 'state', 'zip',
  // #519 — promoted from starred-but-unchecked to genuinely required. Intake
  // already collects it (`billingContact.phone`) and maps it through.
  //
  // (#544 has since taken `billingPhone` back OFF this list — CTIA does not ask
  // for it. What survives of #519 here is `billingAddress`, and the RULE, which
  // is that the `*` and the check are one list. Kept because the reasoning is
  // still the reasoning; only the one field moved.)
  //
  // `billingAddress` joins them here as #518's one-line SPA change, which is
  // the whole payoff of deriving the star: #519 left it out because it had no
  // intake path — requiring it then would have stranded every intake-sourced
  // case behind an address the customer was never asked for — and #518 added
  // `billingContact.address` to the contract and the mapper. Adding the key is
  // all that was needed, and the `*` comes back WITH the check rather than
  // ahead of it, which was the ruling.
  // #544 — `billingPhone`, `authorizerTitle` and `callReason` left this list.
  //
  // All three were required only because THIS BAR asked for them: #518 promoted
  // the first two in the intake contract to match the staff gate, and the ruling
  // on #544 is that the gate itself was above the CTIA minimum. So the relaxation
  // has to happen here as well as in the contract, and in the same commit —
  // relaxing intake alone would let an application in that this gate still
  // refused, which is exactly the stranding #518 closed.
  //
  // They are still COLLECTED and still rendered, and an analyst can still fill
  // them in. What changed is that their absence no longer blocks a submit.
  'billingContact', 'billingEmail', 'billingAddress',
  'authorizerName', 'authorizerEmail',
  'displayName',
];

// #544 — AT LEAST ONE OF THESE, which is a different claim from "required".
// BOTH IS ACCEPTED: a registrant holding a FEIN and a D-U-N-S number may send
// both, and neither this gate nor the contract refuses that.
//
// CTIA (BCBP item 4) accepts the FEIN **or** the D-U-N-S number — an inclusive
// "or" — and the intake contract says so with an `anyOf` over the two
// (apps/api/src/routes/intake.ts). DO NOT "tidy" that into a `oneOf`: `oneOf` is
// the exclusive-or this was first drafted as, and it recreates the very defect
// #544 removed, pointed the other way — it would turn an applicant away for
// supplying MORE identification than the minimum. intake.ts spells the reasoning
// out at the `anyOf`; this comment is the mirror of that rule, not a second
// opinion on it.
//
// It is a list rather than two hard-typed checks for the #519 reason: the gate
// below reads it AND `fieldRow` reads it to draw the marker, so a field cannot
// be marked without being checked.
//
// `dunsBradstreet`, not `duns`: this is `onboarding.data`'s vocabulary, which
// `createEmptyOnboarding()` owns and this tab has always rendered as "Dun &
// Bradstreet Number". The intake mapper is what bridges the two names.
const IDENTIFIER_FIELDS: readonly string[] = ['fein', 'dunsBradstreet'];

// The labels the two identifier fields are rendered with. The `fieldRow` calls
// below read them from here rather than carrying their own strings, so the
// footnote under the card cannot end up naming the fields something other than
// what the inputs above it are called.
const IDENTIFIER_LABELS: Record<string, string> = {
  fein: 'FEIN / Tax ID',
  dunsBradstreet: 'Dun & Bradstreet Number',
};

// #544 — the registrant's attestations the submit refuses without. Read as a
// list so the two warranties #544 added did not have to be typed into the gate,
// the card's border colour and the label separately.
//
// Mirrors REGISTRANT_CERTIFICATIONS in apps/api/src/routes/intake.ts. That one
// is the contract; this one guards staff-entered data and is what an
// intake-sourced case's mapped values are checked against. Change one, change
// the other in the same commit.
//
// #589 — MECHANICALLY TIED, not merely paired by this comment: the
// "REQUIRED_CERTIFICATIONS matches REGISTRANT_CERTIFICATIONS" test in
// OnboardingTab.test.tsx reads BOTH files off disk, proves each extraction
// against an exact count of five and the exact keys, and compares IN ORDER —
// both surfaces show these to a person in list order, and this card DISPLAYS
// what the applicant already affirmed on the portal form. It is read as text
// rather than imported because this list is deliberately not exported; keep the
// declaration's formatting (single-quoted entries) or update that regex with it.
// docs/CROSS_PACKAGE_CONSTANTS.md lists every pair.
const REQUIRED_CERTIFICATIONS: readonly string[] = [
  'certNoShaft', 'certIpOwnership', 'certDmcaProcess',
  'certTnRights', 'certAuthorityToSubmit',
];

// The short name and the full statement for each, so the card, the border and
// the refusal message are one table. The statements are the ones the customer
// portal puts to the applicant (apps/portal/src/pages/SubmissionForm.tsx) —
// they have to be the same words, because on an intake-sourced case these boxes
// are DISPLAYING what the registrant already affirmed rather than asking.
const CERTIFICATION_LABELS: Record<string, string> = {
  certNoShaft: 'No SHAFT Content',
  certIpOwnership: 'IP Ownership',
  certDmcaProcess: 'DMCA & Complaint Process',
  certTnRights: 'Rights to the Telephone Numbers',
  certAuthorityToSubmit: 'Authority to Submit',
};

const CERTIFICATION_STATEMENTS: Record<string, string> = {
  certNoShaft: 'Registrant certifies that Call Logo, Call Reason, and Caller Display Name do not include SHAFT content',
  certIpOwnership: 'Registrant certifies IP in Call Logo/Display Name is owned, licensed, or authorized and does not willfully infringe third-party rights',
  certDmcaProcess: 'Registrant has DMCA notice/takedown process and consumer complaint handling in place (email, phone, privacy policy)',
  certTnRights: 'Registrant certifies the Caller has the requisite rights to the telephone numbers submitted for branding (BCBP representations and warranties)',
  certAuthorityToSubmit: 'Registrant certifies it has the requisite rights to submit those telephone numbers and to act on the Caller\u2019s behalf (BCBP representations and warranties)',
};

// #567 — WHO THE AUTHORIZER IS, which nothing on a case used to say.
//
// BCBP footnote 3: where the Caller uses an authorized agent as its Authorizer
// rather than one of its own employees, "the parties must submit evidence that
// the Caller has authorized the agent". #544 collected the registrant's
// ASSERTION of that authority (`certAuthorityToSubmit`) and nothing more, so an
// agent-authorized application was indistinguishable from an employee one and
// the footnote applied to a set 382 could not identify.
//
// Mirrors AUTHORIZER_RELATIONSHIPS in apps/api/src/routes/intake.ts, which is
// the contract; this one guards staff-entered data and is what an
// intake-sourced case's mapped value is checked against. Change one, change the
// other in the same commit — the same arrangement REQUIRED_CERTIFICATIONS has,
// including its #589 pin: the "AUTHORIZER_RELATIONSHIPS matches the copy in
// apps/api/src/routes/intake.ts" test in OnboardingTab.test.tsx reads both files
// off disk and compares in order, because both put the same two words in front
// of a person in that sequence. docs/CROSS_PACKAGE_CONSTANTS.md lists every pair.
const AUTHORIZER_RELATIONSHIPS: readonly string[] = ['employee', 'authorized_agent'];

const AUTHORIZER_RELATIONSHIP_LABELS: Record<string, string> = {
  employee: 'Employee of the caller',
  authorized_agent: 'Authorized agent acting for the caller',
};

// The declared reference an agent authorizer's application carries, in the
// order the analyst reads it. A LIST because the gate reads it, the card
// renders from it and the refusal names it — one list, so a field cannot be
// asked for without being checked (#519's rule).
//
// There is no document here and there is not meant to be: intake collects a
// declared REFERENCE and no file (user decision, 2026-09-02). What closes the
// footnote-3 loop is the attestation below — an analyst who has seen the
// document says so, against their own id and the time they said it.
const AGENT_AUTHORIZATION_FIELDS: readonly string[] = [
  'agentAuthorizationTitle', 'agentAuthorizationDate', 'agentAuthorizationSignatory',
];

const AGENT_AUTHORIZATION_LABELS: Record<string, string> = {
  agentAuthorizationTitle: 'Authorization Document',
  agentAuthorizationDate: 'Document Date',
  agentAuthorizationSignatory: 'Signed for the Caller by',
};

// #721 — WHAT 382 CALLS THE DOCUMENT NOW, and the sentences that report the
// registrant's own statement about it.
//
// MIRRORS `AGENT_AUTHORIZATION_DOCUMENT_TITLE` in apps/api/src/routes/intake.ts,
// which is the contract and the authority: the server writes that constant into
// `agentAuthorizationTitle` on every agent application filed since #721, and the
// fallback mapper below writes it too. Pinned to that file by the
// "AGENT_AUTHORIZATION_DOCUMENT_TITLE matches the copy in
// apps/api/src/routes/intake.ts" test in OnboardingTab.test.tsx, which reads the
// route off disk — see docs/CROSS_PACKAGE_CONSTANTS.md. #589's caveat applies in
// the usual direction: an api-only change never runs that test.
const AGENT_AUTHORIZATION_DOCUMENT_TITLE = 'Letter of Authorization';

// The three readings of `agentAuthorizationSubmitted`, and the third is the one
// that has to be right.
//
// `true` is the registrant's statement. `false` is a case an analyst typed in at
// the desk, where `createEmptyOnboarding()` seeds the key and no registrant was
// ever involved. ABSENT is an agent application filed through intake BEFORE this
// question existed — nobody was asked, and reporting that as "the customer has
// not said they sent it" would send an analyst chasing a refusal nobody made.
// The same distinction `showRelationship` draws on the customer's own read-back.
const AGENT_SUBMITTED_LABELS: Record<string, string> = {
  yes: 'Customer states the Letter of Authorization has been submitted to 382.',
  no: 'No customer statement \u2014 this case was entered at the 382 desk rather than '
    + 'filed through the customer portal.',
  unasked: 'Not asked \u2014 this application was filed before the portal began asking.',
};

function agentSubmittedReading(value: unknown): string {
  if (value === true) return AGENT_SUBMITTED_LABELS.yes;
  if (value === false) return AGENT_SUBMITTED_LABELS.no;
  return AGENT_SUBMITTED_LABELS.unasked;
}

function isBmpUrl(url: string): boolean {
  const path = url.split(/[?#]/, 1)[0];
  return /\.bmp$/i.test(path);
}

// Mirrors `optionalString` in apps/api/src/workers/settlement-delivery.ts —
// deliberately, character for character in behaviour (issue #485).
//
// Trim is the EMPTINESS test and nothing else: it decides whether a value
// counts as present, and then the RAW string is what gets returned and
// measured. The gate used to measure `.trim().length` instead, so a value of
// exactly-limit-plus-a-trailing-space passed Submit Onboarding and then
// dead-lettered at settlement weeks later — the delayed failure this gate was
// added to prevent (live finding #78). CTIA counts the trailing space; the two
// measurements have to be the same one or the earlier gate is worse than none.
//
// The type guard is `optionalString`'s too. `onboarding.data` is jsonb, so a
// non-string can reach here, and `.trim()` on it threw AT RENDER, not on submit:
// `rcdField` below calls this function on every render to draw the live inline
// warning, so a single numeric value in the jsonb took the WHOLE TAB down — no
// form, no data, nothing to correct the bad value with (#485).
//
// Returning '' is right for MEASURING — there is no length to check — but '' is
// not the same as "fine", and treating it as such is what #502 fixed. See
// `isNonText` below: the caller asks that question separately, first.
//
// If this ever needs to change, change settlement-delivery.ts in the same
// commit. The RCD & SHAFT tab's commit guard is the third copy (#479), and the
// customer portal's apps/portal/src/lib/validation.ts is the fourth.
//
// #589 — A BEHAVIOURAL PAIR, so its pin is a different shape from the list pins
// above and the difference is deliberate. There is no list to compare, so the
// "rawClaim is the same rule as the API copies" block in OnboardingTab.test.tsx
// reads each API copy's PREDICATE out of source and requires all three to be
// `typeof value === 'string' && value.trim() !== ''` character for character,
// then drives a shared fixture table of values through `ctiaOnboardingErrors`
// so the rule is asserted as behaviour on this side and as source text on
// theirs. Rewriting the predicate on either side — even to something equivalent
// — fails that test by name. See docs/CROSS_PACKAGE_CONSTANTS.md.
function rawClaim(value: unknown): string {
  return typeof value === 'string' && value.trim() !== '' ? value : '';
}

// A value that is PRESENT but is not text (#502).
//
// null and undefined are absent, not wrong: the form leaves a field unset that
// way and the emptiness checks already report it as empty, which is the better
// message. Everything else — a number, a boolean, an object, a list — is a value
// somebody put there that cannot be a claim.
//
// Why this is a hard error rather than something to coerce or ignore: #485 made
// `rawClaim` type-guard so the tab would stop crashing on such a value, and that
// left the OPPOSITE failure on the optional field. A non-string callLogoUrl
// measured as '', so the length and .bmp checks had nothing to complain about,
// Submit succeeded, and the value went to the case — where
// settlement-delivery.ts's `optionalString` also read it as absent and its
// conditional spread OMITTED icn_url. The RCD shipped with NO CALL LOGO and
// nothing raised anywhere: a claim silently altered on its way to CTIA, which is
// the one failure class this module refuses (see the trim-on-store decision in
// #485). Refusing the submit is the loud, fixable-at-data-entry alternative.
//
// Reachability is narrow — the intake API enforces type:string,
// mapIntakeToOnboardingData coerces, and the form only ever writes strings — so
// the ways in are the case PATCH's unvalidated `onboarding` body (typed
// `additionalProperties: true` in apps/api/src/routes/cases.ts) or a legacy or
// hand-edited row. Narrow is not the same as never, and silent is the problem.
function isNonText(value: unknown): boolean {
  return value !== undefined && value !== null && typeof value !== 'string';
}

// #766 — THE GATES' OWN PREDICATES, exported so the case progress tracker
// (domain/case-progress.ts) lists open items by asking exactly what the gate
// asks. A tracker that restated a gate would drift from it; one that imports it
// cannot. Each is still used here, by the control it guards.
//
// The form's data, with the #146 read-fallback for an intake promotion that
// carries only the raw payload — what every field on this tab renders.
export function onboardingFormData(ob: { data?: Record<string, any>; intake?: Record<string, any> } | null | undefined): Record<string, any> { // eslint-disable-line @typescript-eslint/no-explicit-any
  if (!ob) return {};
  return (ob.data && Object.keys(ob.data).length > 0)
    ? ob.data
    : (ob.intake ? mapIntakeToOnboardingData(ob.intake) : {});
}

// The REQUIRED_FIELDS Submit Onboarding reports as empty, in list order.
export function missingRequiredFields(data: Record<string, unknown>): string[] {
  return REQUIRED_FIELDS.filter(f => rawClaim(data[f]) === '');
}

// An LOA is required and nobody has verified it: the warning edge on the LOA
// card below.
export function loaOutstanding(c: Pick<Case, 'loa'>): boolean {
  return !!c.loa?.required && !c.loa?.verified;
}

// What to call the offending value in the error, so the analyst reads something
// about their data rather than about JavaScript.
function typeNoun(value: unknown): string {
  if (Array.isArray(value)) return 'a list';
  switch (typeof value) {
    case 'number':
    case 'bigint':
      return 'a number';
    case 'boolean':
      return 'a true/false value';
    case 'object':
      return 'an object';
    default:
      return `a ${typeof value}`;
  }
}

// The three branded-call claims, with the names the form gives them.
const CLAIM_FIELD_LABELS = {
  displayName: 'Caller Display Name',
  callReason: 'Call Reason',
  callLogoUrl: 'Call Logo URL',
} as const;

export type OnboardingFieldError = { field: string; message: string };

// The order the accepted-change card renders in — the same order the customer's
// own form and the staff queue use, so one value is in one place on every
// screen that shows it. A field missing here is an accepted change nothing shows.
const COMPANY_PROFILE_FIELDS = [
  'legalName', 'dbaName', 'businessAddress',
  'businessContact', 'supportContact', 'escalationContact', 'billingContact',
] as const;

// ---- #685: the billing address, in four parts -------------------------------
//
// The registered address has always been four fields and the billing address,
// added by #518, was one free-text box holding the same four facts. The intake
// contract split it (`BILLING_ADDRESS_FIELDS` in apps/api/src/routes/intake.ts);
// this is the staff side of that.
//
// TWO SHAPES REACH THIS TAB AND BOTH ARE LEGITIMATE. A case promoted since the
// split carries `billingStreet`/`billingCity`/`billingState`/`billingZip`; every
// case already in the system carries only `billingAddress`, and it stays that
// way — the ruling on #685 is that stored data is displayed verbatim, with no
// migration and no parsing. Splitting a stored line on commas would invent a
// city out of whatever came second, and a wrong structured address reads on this
// tab as a checked fact.
const BILLING_ADDRESS_PARTS: ReadonlyArray<{ field: string; label: string }> = [
  { field: 'billingStreet', label: 'Billing Street Address' },
  { field: 'billingCity', label: 'Billing City' },
  { field: 'billingState', label: 'Billing State' },
  { field: 'billingZip', label: 'Billing ZIP' },
];

/**
 * The one-line rendering of a four-part billing address.
 *
 * `billingAddress` is on `REQUIRED_FIELDS` here and on `VETTING_REQUIRED_FIELDS`
 * in apps/api/src/routes/cases.ts, so it is the field both gates read — and it
 * has to stay truthful while an analyst edits the parts beside it. It is a JOIN
 * and never a parse.
 *
 * A SECOND COPY LIVES IN THE API (`composeBillingAddress` in
 * apps/api/src/routes/intake.ts), which composes the same line at promotion.
 * Nothing downstream parses the result — no CTIA field derives from it and both
 * gates test only that it is non-empty — so the two copies have to agree in
 * spirit rather than byte for byte, and each is tested against hard-typed
 * expected strings on its own side. See docs/CROSS_PACKAGE_CONSTANTS.md.
 */
export function composeBillingAddress(parts: Record<string, any>): string { // eslint-disable-line @typescript-eslint/no-explicit-any
  const str = (v: any): string => (typeof v === 'string' ? v.trim() : ''); // eslint-disable-line @typescript-eslint/no-explicit-any
  const locality = [str(parts.state), str(parts.zip)].filter(x => x !== '').join(' ');
  return [str(parts.street), str(parts.city), locality].filter(x => x !== '').join(', ');
}

/** Whether this case's onboarding data carries the post-#685 structured shape. */
export function hasStructuredBillingAddress(data: Record<string, any>): boolean { // eslint-disable-line @typescript-eslint/no-explicit-any
  return BILLING_ADDRESS_PARTS.some(({ field }) => rawClaim(data[field]) !== '');
}

// Read-side fallback for cases promoted from intake BEFORE the promote handler
// learned to populate onboarding.data (issue #146). Those rows carry the raw
// intake payload under onboarding.intake but no onboarding.data, so the tab
// rendered an empty form. This projects the raw payload into the flat field
// names the tab reads — mirrors mapIntakeToOnboardingData in the intake route.
// Newly promoted cases already have onboarding.data and never hit this path.
//
// KEEP IT IN STEP WITH THE INTAKE ROUTE'S COPY (#518). This is a second mapper
// over the same payload shape, and the value it feeds is `d` — which is both
// what the form renders AND what the submit gate reads. A key missing here does
// not merely render blank: on a fallback case it makes a REQUIRED field look
// empty and a certification the registrant actually made look unmade, which is
// the #518 bug reappearing on the legacy path. When the intake contract gains a
// field, it gains one here in the same commit.
export function mapIntakeToOnboardingData(payload: Record<string, any>): Record<string, any> {
  const str = (v: any): string => (typeof v === 'string' ? v : '');
  const contact = (v: any): Record<string, any> => (v && typeof v === 'object' ? v : {});
  const billing = contact(payload.billingContact);
  const authorizer = contact(payload.authorizerContact);
  // #567 — `contact()` for its type guard only, not its field names.
  const agentAuth = contact(payload.agentAuthorization);
  return {
    legalName: str(payload.legalName),
    fein: str(payload.fein),
    // #544 — the DUNS alternative, under this tab's name for it. Without it a
    // legacy DUNS-only fallback case shows no identifier at all and the submit
    // gate refuses on an identifier that is sitting in `onboarding.intake`.
    dunsBradstreet: str(payload.duns),
    website: str(payload.website),
    orgType: str(payload.orgType),
    jurisdiction: str(payload.jurisdiction),
    // #771 — the SIC pair, mirroring the intake route's copy. #529/#686 added
    // both to the contract without adding them here; `sicCode` joining
    // REQUIRED_FIELDS is what makes the gap matter, since a fallback case that
    // carried a code would otherwise be refused for one it had given.
    sicCode: str(payload.sicCode),
    sicDescription: str(payload.sicDescription),
    address: str(payload.address),
    city: str(payload.city),
    state: str(payload.state),
    zip: str(payload.zip),
    billingContact: str(billing.name),
    billingEmail: str(billing.email),
    billingPhone: str(billing.phone),
    // #685 — the four parts, plus the one-line field composed from them.
    //
    // KEEP THIS IN STEP WITH THE INTAKE ROUTE'S COPY, which is what the comment
    // above this function is about. `billingAddress` stays populated because it
    // is the key `REQUIRED_FIELDS` below names and the key the API's
    // `VETTING_REQUIRED_FIELDS` names: a fallback case that lost it would fail
    // the submit gate on an address the applicant HAD given.
    //
    // A pre-#685 payload has no parts, `composeBillingAddress` returns '' for
    // it, and the stored one-liner lands verbatim. Nothing parses it.
    billingStreet: str(billing.street),
    billingCity: str(billing.city),
    billingState: str(billing.state),
    billingZip: str(billing.zip),
    billingAddress: composeBillingAddress(billing) || str(billing.address),
    authorizerName: str(authorizer.name),
    authorizerEmail: str(authorizer.email),
    authorizerTitle: str(authorizer.title),
    // #567 — the relationship and its declared reference. Keep the default in
    // step with the intake route's copy: a payload filed before the field
    // existed reads as `employee`, which is the reading that gates nothing and
    // leaves a legacy case exactly where it was. Reading it as an agent instead
    // would strand every pre-#567 case behind an attestation about a document
    // nobody was ever asked for.
    authorizerRelationship:
      payload.authorizerRelationship === 'authorized_agent' ? 'authorized_agent' : 'employee',
    // #721 — MIRRORS the intake route's copy: the title is 382's constant on an
    // agent application that did not send one, and the applicant's own words
    // wherever they did. This mapper runs on a case with no `onboarding.data`,
    // which is by construction a payload filed before #721 — so in practice it
    // takes the stored title every time, and the fallback is here because the
    // rule this function is written to is "when the intake contract gains a
    // field, it gains one here in the same commit".
    agentAuthorizationTitle:
      payload.authorizerRelationship === 'authorized_agent'
        ? (str(agentAuth.documentTitle) || AGENT_AUTHORIZATION_DOCUMENT_TITLE)
        : str(agentAuth.documentTitle),
    agentAuthorizationDate: str(agentAuth.documentDate),
    agentAuthorizationSignatory: str(agentAuth.signatory),
    // #721 — the registrant's claim that the letter is already with 382,
    // MIRRORING the intake route's copy including its omission.
    //
    // THE KEY IS OMITTED, NOT SET FALSE, when an agent payload never carried
    // the answer — and on THIS mapper that is the ordinary case, not the edge.
    // It runs only on a case with `onboarding.intake` and no `onboarding.data`
    // (the #146 read-fallback), which is by construction an application filed
    // before this question existed. Writing `false` would render every one of
    // them as "no customer statement: this case was entered at the 382 desk",
    // which is false about a case that arrived through the customer portal.
    // Absent is what `agentSubmittedReading` turns into "not asked".
    //
    // An EMPLOYEE payload keeps `false`; the block that reads it is hidden on
    // such a case, so nothing renders either way.
    ...(payload.authorizerRelationship === 'authorized_agent'
      && agentAuth.submittedToBcid === undefined
      ? {}
      : { agentAuthorizationSubmitted: agentAuth.submittedToBcid === true }),
    displayName: str(payload.displayName),
    callReason: str(payload.callReason),
    callLogoUrl: str(payload.callLogoUrl),
    originatingCarrier: str(payload.originatingCarrier),
    // #518 — booleans, so they take `=== true` rather than `str()`. Without
    // them a fallback-rendered case shows three unticked boxes for
    // certifications the registrant did make, and the submit gate refuses on
    // attestations that are sitting right there in `onboarding.intake`.
    certNoShaft: payload.certNoShaft === true,
    certIpOwnership: payload.certIpOwnership === true,
    certDmcaProcess: payload.certDmcaProcess === true,
    // #544 — the two BCBP authority warranties, for the same reason.
    certTnRights: payload.certTnRights === true,
    certAuthorityToSubmit: payload.certAuthorityToSubmit === true,
  };
}

// Per-field CTIA violations for the branded-call fields. Pure so the submit
// gate and the live inline warnings share one source of truth (and it can be
// unit-tested without rendering the tab).
export function ctiaOnboardingErrors(data: Record<string, any>): OnboardingFieldError[] {
  const errors: OnboardingFieldError[] = [];
  // #502 — asked FIRST, and of every claim field, because `rawClaim` answers ''
  // for a non-string and '' passes every check below it. The consequence is the
  // one named on `isNonText`: the value reaches CTIA as no value at all.
  //
  // A field reported here is not also reported for length or format. It measures
  // 0 characters through `rawClaim`, so those checks stay quiet on their own —
  // one defect, one error, and the error names the actual defect.
  for (const field of ['displayName', 'callReason', 'callLogoUrl'] as const) {
    if (isNonText(data[field])) {
      errors.push({
        field,
        message: `${CLAIM_FIELD_LABELS[field]} is not text — the stored value is ${typeNoun(data[field])}. `
          + 'Re-enter it: a value that is not text reaches CTIA as no value at all.',
      });
    }
  }
  const displayName = rawClaim(data.displayName);
  if (displayName.length > CTIA_ONBOARDING_LIMITS.displayName) {
    errors.push({ field: 'displayName', message: `Caller Display Name is ${displayName.length} characters; CTIA limits nam to ${CTIA_ONBOARDING_LIMITS.displayName}.` });
  }
  // #541 — TWO different caps, chosen by whether the value is a vetted one.
  // A pre-approved reason may run to the CTIA Platform's 64-character
  // `default_crn` width; a CUSTOM one is capped at 35 by the BCBP. Picking one
  // limit per value rather than testing both keeps an over-long custom reason
  // from reporting two errors that disagree about the number.
  //
  // This gate only ever runs on a DRAFT onboarding being submitted, so applying
  // the custom cap here does not reach back and invalidate a registration that
  // is already live — those are past this gate, and the RCD & SHAFT tab shows
  // them as stored. It binds on data being entered now, which is the point.
  const callReason = rawClaim(data.callReason);
  if (callReason !== '') {
    const custom = !isApprovedCallReason(callReason);
    const limit = custom ? CUSTOM_CALL_REASON_MAX : CTIA_ONBOARDING_LIMITS.callReason;
    if (callReason.length > limit) {
      errors.push({
        field: 'callReason',
        message: custom
          ? `Custom Call Reason is ${callReason.length} characters; the BCBP limits a custom reason to ${limit}. Pick a pre-approved reason or shorten it.`
          : `Call Reason is ${callReason.length} characters; CTIA limits default_crn to ${limit}.`,
      });
    }
  }
  const logo = rawClaim(data.callLogoUrl);
  if (logo.length > 0) {
    if (logo.length > CTIA_ONBOARDING_LIMITS.callLogoUrl) {
      errors.push({ field: 'callLogoUrl', message: `Call Logo URL is ${logo.length} characters; CTIA limits icn_url to ${CTIA_ONBOARDING_LIMITS.callLogoUrl}.` });
    }
    // The FORMAT question is asked of the trimmed path, not the raw string:
    // "does this end in .bmp" is about the filename, and a stray trailing space
    // is already reported above as the length problem it is. Reporting one
    // pasted space twice, once as "too long" and once as "not a .bmp", would
    // send an analyst looking for a file-type problem that is not there.
    if (!isBmpUrl(logo.trim())) {
      errors.push({ field: 'callLogoUrl', message: 'Call Logo URL must point to a .bmp file — CTIA requires the call logo (icn) to be a 256×256 24-bit bitmap.' });
    }
  }
  return errors;
}

/**
 * The fold-forward: the customer's application becoming 382's attested claim.
 *
 * This runs on Submit Onboarding and is the moment `cases.rcd` first gets
 * values. Two things about it are load-bearing (#479-2 and #479-4):
 *
 * IT DOES NOT OVERWRITE AN EXISTING ATTESTED VALUE. The previous version read
 * `data.displayName || c.rcd.displayName`, so the customer's value won whenever
 * it was non-empty — an analyst who corrected the display name on the RCD tab
 * and later clicked Submit Onboarding had their correction silently replaced by
 * the original, with no prompt and no audit entry distinguishing it from any
 * other submit. The precedence is now the same one `buildDeliveryPlan` uses:
 * what 382 attested wins over what was requested. A second submit therefore
 * fills in gaps and changes nothing else, which is what makes re-submitting
 * safe. (The RCD tab now also refuses edits until the first submit, so the two
 * halves agree: before submit there is nothing attested to protect, and after
 * it there is.)
 *
 * IT CARRIES THE LOGO. `callLogoUrl` was the one branded-call claim that never
 * reached `rcd` — two of the three lived in one place and the third somewhere
 * else, which is how nobody noticed the review tab was not showing it.
 *
 * Note that delivery still reads `icn_url` from `onboarding.data`, NOT from this
 * copy, and that is on purpose: the RCD tab does not let anyone edit the logo
 * URL, so the copy can only ever be a snapshot, and preferring a snapshot that
 * cannot be edited would mean a corrected URL on this tab never reached CTIA.
 * The copy is the record of what was attested. If a future change makes the URL
 * editable on the RCD tab, that is the moment to add the `rcd.callLogoUrl ??`
 * fallback in settlement-delivery.ts — not before.
 */
export function foldRcdForward(
  rcd: Record<string, any> | null | undefined,
  data: Record<string, any>,
): Record<string, any> {
  const current = rcd ?? {};
  const keep = (attested: unknown, requested: unknown): string | undefined => {
    const attestedStr = typeof attested === 'string' && attested.trim() !== '' ? attested : undefined;
    const requestedStr = typeof requested === 'string' && requested.trim() !== '' ? requested : undefined;
    return attestedStr ?? requestedStr;
  };
  const folded: Record<string, any> = { ...current };
  for (const field of ['displayName', 'callReason', 'callLogoUrl'] as const) {
    const value = keep(current[field], data[field]);
    if (value !== undefined) folded[field] = value;
  }
  return folded;
}

export function OnboardingTab({ c }: { c: Case }) {
  const { now, updateCase, mergeCases } = useAppData();
  const { setModal, setModalData } = useUIState();
  const user = getCurrentUser()!;
  const canApprove = user.canApprove;

  const ob = c.onboarding || createEmptyOnboarding();
  // #146 read-fallback: pre-fix intake promotions carry onboarding.intake but
  // no onboarding.data, so map the raw payload on read. Once the analyst edits
  // a field, onboarding.data exists and takes over (drafts commit into it).
  const d = onboardingFormData(ob);
  // #810 — A SAVED NEW LOGO URL CHANGES THE CASE AGAIN A MOMENT LATER. The
  // PATCH queues a snapshot, and when the worker settles a new image it bumps
  // the case version (settleSnapshot in the API), so the analyst's NEXT edit
  // on this tab would carry a stale If-Match and 409. So once the stored URL
  // changes, wait for the snapshot OF THAT URL to settle — its existence is
  // what proves the PATCH landed — and re-read the case. Not on mount, and not
  // cancelled on unmount: CaseDetail mounts only the active tab, and the edit
  // this protects may be made on another one. Bounded (about two minutes), and
  // all it does is re-read the case.
  const storedLogoUrl = typeof d.callLogoUrl === 'string' ? d.callLogoUrl : '';
  const lastLogo = useRef({ caseId: c.id, url: storedLogoUrl });
  useEffect(() => {
    const previous = lastLogo.current;
    lastLogo.current = { caseId: c.id, url: storedLogoUrl };
    if (previous.caseId !== c.id || previous.url === storedLogoUrl || storedLogoUrl.trim() === '') return;
    void waitForLogoSnapshot(c.id, storedLogoUrl).then((settled) => {
      if (settled?.outcome !== 'ok') return;
      getCase(c.id).then((fresh) => mergeCases([fresh])).catch(() => { /* the #148 poll catches up */ });
    });
  }, [c.id, storedLogoUrl, mergeCases]);
  // #478 — the lossless record of what an accepted company-profile change
  // carried in, in the change request's own vocabulary. Its address and legal
  // name are mirrored onto the flat keys the fields above render; the three
  // contacts and the address parts those keys cannot hold (line 2, country)
  // exist only here, which is why the tab renders it.
  const companyProfile = (ob.companyProfile ?? null) as Record<string, unknown> | null;
  const isEditable = ob.status === 'DRAFT' || ob.status === 'SUBMITTED';
  // #775 — the customer declared numbers 382 does not provide; the LOA
  // requirement is theirs (#822) and its verify lives on the TNs tab (#834).
  const customerDeclaredLoa = c.loa?.notProvidedBy382 === true;
  // H-6: text fields buffer locally and PATCH on blur (or fold into the
  // submit PATCH); each checkbox/select click is a single deliberate action
  // and commits immediately. Keys: onboarding.data fields by name; ob-level
  // and loa-level fields prefixed 'ob:' / 'loa:' in the same map.
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  useEffect(() => setDrafts({}), [c.id]);
  const setDraft = (key: string, value: string) => setDrafts(prev => ({ ...prev, [key]: value }));
  const takeDraft = (key: string): string | undefined => {
    const value = drafts[key];
    if (value !== undefined) setDrafts(prev => { const { [key]: _, ...rest } = prev; return rest; });
    return value;
  };
  const commitDataField = (field: string) => {
    const value = takeDraft(field);
    if (value === undefined || value === (d[field] || '')) return;
    updateCase(c.id, { onboarding: { ...ob, data: { ...d, [field]: value } } });
  };
  // #685 — editing one of the four parts recomposes the one-line
  // `billingAddress` in the SAME write.
  //
  // That field is what `REQUIRED_FIELDS` here and `VETTING_REQUIRED_FIELDS` in
  // the API both read, and it is composed at promotion from the parts the
  // applicant filed. Leaving it alone while an analyst corrects a ZIP would
  // leave the gated value quietly disagreeing with the four fields on screen —
  // a stale address nobody could see was stale, on the tab an analyst vets
  // from.
  //
  // ONE `updateCase`, not two: same-tick case PATCHes are serialised per case
  // by If-Match chaining, and two writes for one edit is the shape that rule
  // warns about. Both keys go in one `data`.
  const commitBillingPart = (field: string) => {
    const value = takeDraft(field);
    if (value === undefined || value === (d[field] || '')) return;
    const data = { ...d, [field]: value };
    updateCase(c.id, {
      onboarding: {
        ...ob,
        data: {
          ...data,
          billingAddress: composeBillingAddress({
            street: data.billingStreet, city: data.billingCity,
            state: data.billingState, zip: data.billingZip,
          }),
        },
      },
    });
  };
  const updateOb = (field: string, value: any) => {
    const newData = { ...d, [field]: value };
    updateCase(c.id, { onboarding: { ...ob, data: newData } });
  };
  const submitOnboarding = () => {
    // Fold any not-yet-blurred drafts in, so the click commits what the
    // analyst sees on screen.
    const dataDrafts = Object.fromEntries(Object.entries(drafts).filter(([k]) => !k.includes(':')));
    const data = { ...d, ...dataDrafts };
    const required = REQUIRED_FIELDS;
    // #502 — before the emptiness test, because the emptiness test used to THROW
    // here. `!data[f]?.trim()` optional-chains null and undefined but not a
    // number, so `(42).trim` was an uncaught TypeError out of the click handler:
    // no modal, no submit, no explanation. Two of these fields (displayName,
    // callReason) are also CTIA claims, so this runs ahead of
    // `ctiaOnboardingErrors` and reports them once, here.
    const notText = required.filter(f => isNonText(data[f]));
    if (notText.length > 0) {
      setModal('validation-error');
      setModalData({
        missing: notText,
        message: `${notText.length} required field(s) hold a value that is not text: ${notText.join(', ')}. `
          + 'Re-enter them — a value that is not text cannot be submitted.',
      });
      return;
    }
    const missing = missingRequiredFields(data);
    if (missing.length > 0) { setModal('validation-error'); setModalData({ missing, message: `${missing.length} required field(s) are empty.` }); return; }
    // #693 — A SEPARATE OA-EMAIL CHECK USED TO SIT HERE, and #543's argument for
    // it is intact; only the field it reads changed.
    //
    // That argument: email is the only notification path (#543 removed the API
    // button), so a case nobody can be notified about is un-notifiable, and
    // `emailJobForCaseEvent` refuses such a case SILENTLY. Caught at submit, it
    // is a field an analyst fills in. Caught after a decision, the notice is
    // already overdue, the 30-day clock is already running, and on an
    // UNSUCCESSFUL vet a 12-business-day cure window has already opened against
    // a registrant who was never told.
    //
    // It needed its own check because the address lived on the CASE
    // (`cases.oa_email`, edited on the Overview tab) rather than in
    // `onboarding.data`, so it could not go on REQUIRED_FIELDS. The recipient is
    // the submission's authorizer contact now, billing contact as fallback, and
    // `authorizerEmail` is ALREADY a member of REQUIRED_FIELDS — so the sweep
    // above covers it and a second check would only be a second thing to forget.
    //
    // The server's gate is the one that actually holds (`vettingEntryBlockers`
    // in apps/api/src/routes/cases.ts), and it is deliberately slightly LOOSER
    // than this one: it lets a submission through on a billing email alone,
    // where REQUIRED_FIELDS here asks for both. Looser on the server than in the
    // UI is the safe direction — this refuses nothing the API would accept by
    // mistake, it only asks the analyst for a field the API would have taken.
    // #544 — the identifier, which is one-of-two rather than required.
    //
    // Checked after the emptiness sweep and separately from it, because the
    // question is not "is this field empty" but "did the registrant give us an
    // identifier at all". Reporting `fein` as an empty required field would send
    // an analyst to type a FEIN the applicant may not have, when the D-U-N-S
    // number beside it is a perfectly good answer.
    //
    // The same rule the door applies (`identifierChoiceProblem` in
    // apps/api/src/routes/intake.ts): AT LEAST one, and both is fine. It was
    // drafted as an exclusive-or on both sides and corrected before shipping —
    // refusing a registrant for holding BOTH a FEIN and a D-U-N-S number would
    // be refusing them for being better identified, and an analyst who looks an
    // entity up and adds the second number must not thereby break the submit.
    const identifiers = IDENTIFIER_FIELDS.filter(f => rawClaim(data[f]) !== '');
    if (identifiers.length === 0) {
      setModal('validation-error');
      setModalData({
        missing: [...IDENTIFIER_FIELDS],
        message: 'An entity identifier is required: fill in either the '
          + `${IDENTIFIER_FIELDS.map(f => IDENTIFIER_LABELS[f]).join(' or the ')}. `
          + 'CTIA accepts either one, so whichever the registrant gave is enough — but '
          + 'the registration cannot go out with neither.',
      });
      return;
    }
    // #635 — read off PERSONAL_EMAIL_DOMAINS (domain/constants.ts), which is
    // pinned to the API's contract copy by a disk-read test. This was an
    // inline array literal, and an inline literal cannot be pinned at all:
    // the staff list could fall behind the door's and nothing would say so.
    // The MATCH is unchanged — a bare suffix, not anchored on the `@`.
    const authEmail = (data.authorizerEmail || '').toLowerCase();
    if (PERSONAL_EMAIL_DOMAINS.some(pd => authEmail.endsWith(pd))) { setModal('validation-error'); setModalData({ missing: ['authorizerEmail'], message: "Authorizer email must be a work email from the registrant's domain — personal email addresses (Gmail, Yahoo, etc.) are not permitted per BCID procedures." }); return; }
    // #544 — read off REQUIRED_CERTIFICATIONS, and the message counts the list
    // rather than saying "three". The hard-typed version said "All three" in
    // prose while checking three fields by hand, so adding the two BCBP
    // authority warranties would have meant a gate that checked five and a
    // message that promised three.
    const unaffirmed = REQUIRED_CERTIFICATIONS.filter(f => data[f] !== true);
    if (unaffirmed.length > 0) {
      setModal('validation-error');
      setModalData({
        missing: unaffirmed,
        message: `${unaffirmed.length} of the ${REQUIRED_CERTIFICATIONS.length} registrant `
          + `certifications ${unaffirmed.length === 1 ? 'is' : 'are'} not confirmed: `
          + `${unaffirmed.map(f => CERTIFICATION_LABELS[f] ?? f).join(', ')}. `
          + 'These are the registrant\'s own attestations and all of them are required '
          + 'before submission.',
      });
      return;
    }
    // #567 — THE AUTHORIZED-AGENT GATE (BCBP fn. 3).
    //
    // Only an AGENT-authorizer case meets this at all. An employee-authorizer
    // case is untouched by every line of it, which is the property that keeps
    // the change from stranding the ordinary application — and it is what the
    // `authorizerRelationship !== 'authorized_agent'` guard on the front is for.
    //
    // Two things are checked and both are necessary. The declared reference
    // must actually name a document, because an analyst cannot ask to see
    // "  " — and on a staff-typed case those three fields start empty, so
    // nothing else would have filled them. Then the attestation: an analyst has
    // to say they saw the document. The declaration alone is the REGISTRANT's
    // claim about their own authority, and a self-reported claim is not the
    // evidence CTIA asks for, which is the same boundary the financial
    // references draw at intake.
    //
    // After the certifications and before the CTIA limits, because it is a
    // question about the application's authority rather than about a field's
    // width, and the certifications are the other questions of that kind.
    if (authorizerRelationshipView(ob.intake, data).relationship === 'authorized_agent') {
      const missingAgent = AGENT_AUTHORIZATION_FIELDS.filter(f => rawClaim(data[f]) === '');
      if (missingAgent.length > 0) {
        setModal('validation-error');
        setModalData({
          missing: missingAgent,
          message: `The authorizer is an authorized agent, so the authorization has to be `
            + `identified before this case can be submitted. ${missingAgent.length} field(s) `
            + `are empty: ${missingAgent.map(f => AGENT_AUTHORIZATION_LABELS[f] ?? f).join(', ')}. `
            + 'CTIA requires evidence that the caller authorized the agent (BCBP footnote 3), '
            + 'and an analyst cannot ask for a document nobody has named.',
        });
        return;
      }
      if (data.agentAuthorizationVerified !== true) {
        setModal('validation-error');
        setModalData({
          missing: ['agentAuthorizationVerified'],
          message: 'The authorizer is an authorized agent, so the authorization evidence must '
            + 'be verified before this case can be submitted. Ask for the document named above, '
            + 'check that the signatory could bind the caller, then tick "Authorization evidence '
            + 'verified". The registrant\u2019s own certification is their assertion of that '
            + 'authority \u2014 CTIA asks for evidence of it (BCBP footnote 3), which is this tick.',
        });
        return;
      }
    }
    const ctiaErrors = ctiaOnboardingErrors(data);
    if (ctiaErrors.length > 0) { setModal('validation-error'); setModalData({ missing: ctiaErrors.map(e => e.field), message: `CTIA field limits not met — ${ctiaErrors.map(e => e.message).join(' ')}` }); return; }
    const now2 = new Date().toISOString();
    setDrafts({});
    updateCase(c.id, {
      // #693 — `submittedBy: c.oaName || user.name`. `oaName` is retired and
      // named 382 rather than a person; the signed-in analyst IS who performed
      // this step, so the fallback had nothing left to fall back from.
      onboarding: { ...ob, data, status: 'SUBMITTED', submittedAt: now2, submittedBy: user.name, oaReviewDeadline: addBusinessDays(now2, 10).toISOString() },
      registrant: data.legalName || c.registrant,
      rcd: foldRcdForward(c.rcd, data),
      trust: { ...c.trust, consent: data.consentProcess, optOut: data.optOutProcess, complaintEmail: data.complaintEmail, complaintPhone: data.complaintPhone, privacyUrl: data.privacyUrl, autodialer: data.useAutodialer },
      action: { code: 'ONBOARDING_SUBMITTED', detail: { submittedBy: user.name } },
    });
  };
  const promoteToVetting = () => {
    const result = acceptOnboarding(c, updateCase, 'onboarding-tab');
    if (!result.ok) { setModal('validation-error'); setModalData({ missing: ['agreement'], message: result.message }); }
  };
  // Branded-call field + a live CTIA warning under it. Reads the draft so the
  // warning tracks what's on screen (like the authorizer-email check, H-6),
  // committing on blur. Shares ctiaOnboardingErrors with the submit gate.
  const rcdField = (label: string, field: 'displayName' | 'callLogoUrl') => {
    // `??`, not `||` (#502): the warning has to see the stored value as it is.
    // `||` folds a falsy non-string — 0, false — to '', which is exactly the
    // shape whose warning matters, and would have left the submit refusing a
    // value the field showed no complaint about. A genuinely absent value is
    // still undefined/null and still becomes ''. This feeds the WARNING only;
    // `fieldRow` computes the input's own value.
    const liveValue = drafts[field] ?? (d[field] ?? '');
    const warn = ctiaOnboardingErrors({ [field]: liveValue }).map(e => e.message);
    return (
      <div>
        {fieldRow(label, field)}
        {warn.map((m, i) => (
          <div key={i} style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>⚠ {m}</div>
        ))}
      </div>
    );
  };
  // #541 — the call reason is the one branded-call claim that is a CHOICE, so
  // it does not go through `rcdField`'s free-text input. It keeps everything
  // else `rcdField` gives the other two: the same label row, and the same live
  // `ctiaOnboardingErrors` warning the submit gate reads, so the field cannot
  // complain about something the gate would pass or stay silent about something
  // it would refuse.
  //
  // Choosing an option COMMITS; typing in the custom box drafts and commits on
  // blur, exactly like every other text field on this card. A <select> has no
  // useful blur, and committing per keystroke would fire a PATCH per character
  // into a context that serialises same-tick case writes.
  const commitCallReason = (next: string) => {
    setDrafts(prev => { const { callReason: _drop, ...rest } = prev; return rest; });
    if (next === (d.callReason || '')) return;
    updateCase(c.id, { onboarding: { ...ob, data: { ...d, callReason: next } } });
  };
  const callReasonField = () => {
    // `??` on the stored read, for #502's reason: the warning has to see the
    // stored value as it is rather than folding a falsy non-string to ''.
    const liveValue = drafts.callReason ?? (d.callReason ?? '');
    const warn = ctiaOnboardingErrors({ callReason: liveValue }).map(e => e.message);
    return (
      <div>
        <label htmlFor="ob-callReason" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>
          Call Reason (pre-approved list, or custom ≤{CUSTOM_CALL_REASON_MAX} chars)
        </label>
        <CallReasonSelect
          // Remount per case. `CallReasonSelect` remembers whether the analyst
          // opened the custom box, and this tab is REUSED across cases rather
          // than remounted (see the `setDrafts({})` effect on `c.id` above), so
          // without this the box would stay open on the next case — showing a
          // custom input over a case whose reason is on the approved list.
          key={c.id}
          id="ob-callReason"
          value={typeof liveValue === 'string' ? liveValue : String(liveValue)}
          // The CASE's value, for the mount-time seed only — deliberately not
          // `liveValue`, which is draft-first and, on the render that remounts
          // this control, still holds the OUTGOING case's draft. Coerced the
          // same way `value` is, so a non-string stored value (#502) seeds and
          // renders on the same reading rather than on two.
          storedValue={String(d.callReason ?? '')}
          readOnly={!isEditable}
          onSelect={commitCallReason}
          onCustomInput={next => setDraft('callReason', next)}
          onCustomBlur={() => commitDataField('callReason')}
        />
        {warn.map((m, i) => (
          <div key={i} style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>⚠ {m}</div>
        ))}
      </div>
    );
  };
  // #770 — the CTIA registrant class is a CHOICE of three, so it is a select
  // that writes the code itself, not a free-text input the settlement worker
  // has to guess a code from. Choosing an option commits, as the call reason
  // does. A legacy free-text value ("LLC") matches no option, so the select
  // shows the placeholder and the stored text is named beneath it until an
  // analyst picks a type.
  const orgTypeField = () => {
    const stored = d.orgType === undefined || d.orgType === null ? '' : String(d.orgType);
    const known = ORG_TYPE_LABELS.some(o => o.value === stored);
    // Derived from REQUIRED_FIELDS like every `fieldRow` label (#519).
    const labelText = REQUIRED_FIELDS.includes('orgType') ? 'Organization Type *' : 'Organization Type';
    return (
      <div>
        <label htmlFor="ob-orgType" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>{labelText}</label>
        <select
          id="ob-orgType"
          style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }}
          value={known ? stored : ''}
          onChange={e => { if (e.target.value && e.target.value !== stored) updateOb('orgType', e.target.value); }}
          disabled={!isEditable}
        >
          <option value="" disabled>Choose a type…</option>
          {ORG_TYPE_LABELS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        {stored !== '' && !known && (
          <div style={{ fontSize: 11, color: C.warn, marginTop: 4 }}>Current value: {stored} — choose a type</div>
        )}
      </div>
    );
  };
  const fieldRow = (label: string, field: string, opts: any = {}) => {
    const id = `ob-${field}`;
    // Derived, never typed (#519): the `*` is a reading of REQUIRED_FIELDS, so
    // the promise the label makes and the check the gate runs cannot disagree.
    //
    // #544 adds a second reading for the same reason. The two identifier fields
    // are not required — either one satisfies the gate — so a `*` on them would
    // be a lie in the direction #519 warned about, and NO mark would be a lie in
    // the other direction, since a case with neither cannot be submitted. `*†`
    // says both true things at once, and the footnote under the card says which
    // pair it means.
    const labelText = REQUIRED_FIELDS.includes(field)
      ? `${label} *`
      : IDENTIFIER_FIELDS.includes(field)
        ? `${label} *†`
        : label;
    return (
      <div>
        <label htmlFor={id} style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>{labelText}</label>
        {opts.textarea ? (
          <textarea id={id} style={{ ...styles.textarea, fontSize: 12, minHeight: 60 }} value={drafts[field] ?? (d[field] || '')} onChange={e => setDraft(field, e.target.value)} onBlur={() => commitDataField(field)} readOnly={!isEditable} />
        ) : opts.checkbox ? (
          <div style={styles.checkItem}><input id={id} type="checkbox" checked={d[field] || false} onChange={e => updateOb(field, e.target.checked)} disabled={!isEditable} /><label htmlFor={id} style={{ fontSize: 12 }}>{opts.checkLabel}</label></div>
        ) : (
          <input id={id} style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} value={drafts[field] ?? (d[field] || '')} onChange={e => setDraft(field, e.target.value)} onBlur={() => (opts.commit ?? commitDataField)(field)} readOnly={!isEditable} />
        )}
      </div>
    );
  };
  return (
    <div>
      {/* #478 — an open change-driven re-vet, above everything else on the tab.
          It is the reason the legal name and address below may not be what the
          customer believes they are, so it has to be read before them. */}
      <RevetChangePanel caseId={c.id} />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Onboarding Data Collection</h3>
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Application data collected from Onboarding Agent · Status: <StatusBadge status={ob.status === 'DRAFT' ? 'IN_REVIEW' : ob.status === 'SUBMITTED' ? 'OPEN' : 'COMPLETED'} />
            {ob.submittedAt && <span style={{ marginLeft: 8 }}>Submitted: {fmtDateTime(ob.submittedAt)} by {ob.submittedBy}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {ob.status === 'DRAFT' && <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall }} onClick={submitOnboarding}>Submit Onboarding Data</button>}
          {ob.status === 'SUBMITTED' && user.canEdit && (
            <button style={{ ...styles.btn, ...styles.btnSuccess, ...styles.btnSmall }} onClick={promoteToVetting} disabled={!hasExecutedAgreement(c)}>Send to vetting</button>
          )}
        </div>
      </div>
      <div style={{ ...styles.card, borderLeft: `3px solid ${ob.agreementExecuted ? C.success : C.danger}`, marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: ob.agreementExecuted ? C.success : C.danger }}>
          {ob.agreementExecuted ? '✓' : '⚠'} Executed Agreement {ob.agreementExecuted ? '(Confirmed)' : '(REQUIRED before submission to Settlement Platform)'}
        </h4>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Per Section 2: OA shall ensure a valid executed Agreement is in place with Registrant prior to uploading any Caller Information to Settlement Platform.</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr 1fr', gap: 12, alignItems: 'center' }}>
          <div style={styles.checkItem}>
            <input id="ob-agreementExecuted" type="checkbox" checked={ob.agreementExecuted || false} onChange={e => {
              updateCase(c.id, {
                onboarding: { ...ob, agreementExecuted: e.target.checked, agreementDate: e.target.checked ? new Date().toISOString().split('T')[0] : null },
                action: { code: e.target.checked ? 'AGREEMENT_CONFIRMED' : 'AGREEMENT_REMOVED', detail: { confirmed: e.target.checked } },
              });
            }} />
            <label htmlFor="ob-agreementExecuted" style={{ fontSize: 12, fontWeight: 600 }}>Registrant Agreement executed and on file</label>
          </div>
          <div>
            <label htmlFor="ob-agreementDate" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Agreement Date</label>
            <input id="ob-agreementDate" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} type="date" value={ob.agreementDate || ''} onChange={e => updateCase(c.id, { onboarding: { ...ob, agreementDate: e.target.value } })} />
          </div>
          <div>
            <label htmlFor="ob-agreementRef" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>Agreement Reference #</label>
            <input id="ob-agreementRef" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} value={drafts['ob:agreementRef'] ?? (ob.agreementRef || '')} onChange={e => setDraft('ob:agreementRef', e.target.value)} onBlur={() => {
              const value = takeDraft('ob:agreementRef');
              if (value !== undefined && value !== (ob.agreementRef || '')) updateCase(c.id, { onboarding: { ...ob, agreementRef: value } });
            }} placeholder="OA-2026-XXXX" />
          </div>
        </div>
      </div>
      <div style={{ ...styles.card, marginBottom: 16 }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>OA Processing SLAs</h4>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Review within 10 biz days of collection · Forward to VA within 1 day of completed review</div>
        <div style={{ display: 'flex', gap: 16 }}>
          {ob.oaReviewDeadline && <DeadlineIndicator deadline={ob.oaReviewDeadline} label="OA review (10 biz days)" now={now} />}
          {ob.oaReviewCompletedAt && <span style={{ fontSize: 12, color: C.success }}>✓ Review completed: {fmtDate(ob.oaReviewCompletedAt)}</span>}
          {ob.oaForwardDeadline && ob.oaReviewCompletedAt && <DeadlineIndicator deadline={ob.oaForwardDeadline} label="Forward to VA (1 day)" now={now} />}
          {ob.oaForwardedAt && <span style={{ fontSize: 12, color: C.success }}>✓ Forwarded: {fmtDate(ob.oaForwardedAt)}</span>}
        </div>
        {!ob.oaReviewCompletedAt && ob.status !== 'DRAFT' && (
          <button style={{ ...styles.btn, ...styles.btnGold, ...styles.btnSmall, marginTop: 8 }} onClick={() => {
            const now2 = new Date().toISOString();
            updateCase(c.id, {
              onboarding: { ...ob, oaReviewCompletedAt: now2, oaForwardDeadline: addCalendarDays(now2, 1).toISOString() },
              action: { code: 'OA_REVIEW_COMPLETED', detail: {} },
            });
          }}>Mark Review Complete</button>
        )}
      </div>
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Legal Entity Information</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {fieldRow('Legal Entity Name', 'legalName')}
          {fieldRow(IDENTIFIER_LABELS.fein, 'fein')}
          {fieldRow('DBA / Trade Name', 'dba')}
          {fieldRow('Website URL', 'website')}
          {orgTypeField()}
          {fieldRow('State of Incorporation', 'jurisdiction')}
          {fieldRow('Street Address', 'address')}
          {fieldRow('City', 'city')}
          {fieldRow('State', 'state')}
          {fieldRow('ZIP', 'zip')}
          {fieldRow(IDENTIFIER_LABELS.dunsBradstreet, 'dunsBradstreet')}
          {fieldRow('Corporate Registration Number', 'corpRegNumber')}
          {fieldRow('SIC Code', 'sicCode')}
          {fieldRow('SIC Description', 'sicDescription')}
          {fieldRow('Professional License (if applicable)', 'profLicense')}
        </div>
        {/* #544 — what the `*†` on the two identifier fields means. Derived from
            the same list the gate reads, so it cannot end up naming the wrong
            pair. */}
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 10 }}>
          † At least one of {IDENTIFIER_FIELDS.map(f => IDENTIFIER_LABELS[f]).join(' / ')} is
          required — CTIA accepts either as the entity identifier, so whichever the registrant
          gave is enough.
        </div>
      </div>
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Billing Information</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {fieldRow('Billing Contact Name', 'billingContact')}
          {fieldRow('Billing Email', 'billingEmail')}
          {fieldRow('Billing Phone', 'billingPhone')}
          {/* #685 — WHICHEVER SHAPE THIS CASE IS IN, and never both.
              A case promoted since the address was split gets the four parts,
              each editable, with the gated one-line `billingAddress`
              recomposed from them on every edit. A case already in the system
              gets the single box it has always had, because its address is one
              line and stays one — no migration and no parsing (see
              `composeBillingAddress`).
              Rendering both would put two addresses on one card with nothing
              saying which an invoice goes to. */}
          {hasStructuredBillingAddress(d)
            ? BILLING_ADDRESS_PARTS.map(({ field, label }) => (
              <div key={field}>{fieldRow(label, field, { commit: commitBillingPart })}</div>
            ))
            : fieldRow('Billing Address', 'billingAddress')}
        </div>
        {hasStructuredBillingAddress(d) && (
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 10 }}>
            The registrant gave this address in four parts. The single-line value the
            completion bar checks is composed from them and updates as you edit.
          </div>
        )}
      </div>
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Authorizer Information</h4>
        <div style={{ fontSize: 11, color: C.warn, marginBottom: 8 }}>⚠ Per procedures: must be work email from registrant domain — not Gmail, Yahoo, or similar</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {fieldRow('Authorizer Name', 'authorizerName')}
          <div>
            {fieldRow('Authorizer Email', 'authorizerEmail')}
            {(() => {
              // Read the draft so the warning stays live while typing, even
              // though the value only commits on blur (H-6).
              const ae = drafts.authorizerEmail ?? d.authorizerEmail;
              return ae && (ae.includes('gmail') || ae.includes('yahoo') || ae.includes('hotmail') || ae.includes('outlook.com') || ae.includes('aol')) && (
                <div style={{ fontSize: 11, color: C.danger, marginTop: 4 }}>⚠ Personal email detected — must be company domain</div>
              );
            })()}
          </div>
          {fieldRow('Authorizer Title', 'authorizerTitle')}
        </div>
        {/* #567 — WHO THE AUTHORIZER IS, and the evidence when they are an
            agent (BCBP fn. 3). It sits inside the Authorizer card rather than
            in one of its own because it is a fact ABOUT the person named above,
            and an analyst reading their details is the analyst who needs it. */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          {/* READ-ONLY (#631): no control at all, not a disabled one — a
              `disabled` prop is one deletion from being the gate's off switch
              again. This is the REGISTRANT's declaration and the footnote-3
              gate keys on it; as an editable select an analyst could set an
              agent case to `employee` and the whole gate stopped applying,
              with nothing saying the filed application had said otherwise. No
              `*`, for #519's reason. The note says whose statement it is —
              three different facts, one of which nobody was ever asked. */}
          {(() => {
            // #642 — ONE derivation for the label, the sentence under it
            // and the submit gate. It reads the FILED application whenever
            // there is one, so a case whose stored `authorizerRelationship`
            // was flipped by an analyst before #631 made the field read-only
            // shows what the registrant actually declared — under the
            // sentence saying they declared it, with the gate keyed on it.
            const { relationship: rel, source } = authorizerRelationshipView(ob.intake, d);
            return (
              <>
                <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 3 }}>Relationship to the Caller</div>
                <div id="ob-authorizerRelationship" style={{ fontSize: 12 }}>{AUTHORIZER_RELATIONSHIP_LABELS[rel] ?? rel}</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>{source} Not editable here — it is the registrant’s own declaration, and the gate keys on the filed value. A declaration that is wrong is a company-profile change request from the customer, not a staff edit.</div>
              </>
            );
          })()}
          {authorizerRelationshipView(ob.intake, d).relationship === 'authorized_agent' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 11, color: C.warn, marginBottom: 8 }}>
                ⚠ CTIA requires evidence that the caller authorized this agent (BCBP footnote 3).
                The registrant declares the authorization here; ask to see it and record that you did.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                {AGENT_AUTHORIZATION_FIELDS.map(f => (
                  <div key={f}>{fieldRow(AGENT_AUTHORIZATION_LABELS[f], f)}</div>
                ))}
              </div>
              <div style={{ ...styles.checkItem, marginTop: 12 }}>
                {/* One PATCH per logical action: the tick, the actor and the
                    time go in a single write. `user.id` is the Keycloak sub,
                    which the API's resolvePrincipals() translates — writing the
                    sub and comparing on serverId is the repo's rule (B-2). */}
                <input
                  id="ob-agentAuthorizationVerified"
                  type="checkbox"
                  checked={d.agentAuthorizationVerified === true}
                  onChange={e => {
                    const on = e.target.checked;
                    updateCase(c.id, {
                      onboarding: {
                        ...ob,
                        data: {
                          ...d,
                          agentAuthorizationVerified: on,
                          // Cleared on untick rather than left standing. A
                          // stale attributed timestamp under an unticked box
                          // reads as an attestation somebody withdrew and
                          // somehow still made.
                          agentAuthorizationVerifiedBy: on ? user.id : '',
                          agentAuthorizationVerifiedAt: on ? new Date().toISOString() : '',
                        },
                      },
                    });
                  }}
                  disabled={!isEditable}
                />
                <label htmlFor="ob-agentAuthorizationVerified" style={{ fontSize: 12 }}>
                  Authorization evidence verified — the document named above was produced, and its
                  signatory can bind the caller
                </label>
              </div>
              {d.agentAuthorizationVerified === true && d.agentAuthorizationVerifiedAt && (
                <div style={{ fontSize: 11, color: C.textDim, marginTop: 4 }}>
                  {fmtDateTime(d.agentAuthorizationVerifiedAt)} · {resolveActorName({ actorId: d.agentAuthorizationVerifiedBy })}
                </div>
              )}
              {/* #721 — THE REGISTRANT'S CLAIM, beside 382's finding and never
                  in place of it. The tick above is an analyst saying they saw
                  the document; this line is the customer saying they sent it,
                  and before #721 nothing on the case distinguished "the letter
                  is in somebody's inbox" from "nobody has sent one" — the
                  analyst found out by writing to ask.
                  Rendered as TEXT and not as a control, for #631's reason: it
                  is the registrant's statement and a staff-side checkbox over
                  it would make 382 the author of a claim it only received. */}
              <div
                id="ob-agentAuthorizationSubmitted"
                style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}
              >
                {agentSubmittedReading(d.agentAuthorizationSubmitted)}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* #478 — what an accepted company-profile change carried in. The three
          contacts have no other home on a case: `billingContact` and the
          authorizer above are different people doing different jobs, and
          overwriting either with a support address would be wrong. Rendered
          read-only because it is a record of what the customer asked for and
          382 accepted, not a form — changing it is another change request. */}
      {companyProfile && (
        <div style={{ ...styles.card, borderLeft: `3px solid ${C.gold}` }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 4 }}>
            From an accepted company-profile change
          </h4>
          <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 12 }}>
            Request {String(companyProfile.changeRequestId ?? '')}
            {companyProfile.updatedAt ? ` · carried in ${fmtDateTime(String(companyProfile.updatedAt))}` : ''}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {COMPANY_PROFILE_FIELDS
              .filter((f) => companyProfile[f] !== undefined && companyProfile[f] !== null)
              .map((f) => (
                <div key={f}>
                  <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 3 }}>{COMPANY_FIELD_LABEL[f] ?? f}</div>
                  <div style={{ fontSize: 12 }}>{formatCompanyFieldValue(f, companyProfile[f])}</div>
                </div>
              ))}
          </div>
        </div>
      )}
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Rich Call Data</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {rcdField('Caller Display Name (CTIA nam ≤35 chars)', 'displayName')}
          {callReasonField()}
          <div>
            {rcdField('Call Logo URL (CTIA icn — 256×256 .bmp, URL ≤128 chars)', 'callLogoUrl')}
            {/* #767 — what saving a new URL does, done by the server: the PATCH
                queues a snapshot (routes/cases.ts) and a differing image resets
                the SHAFT review (lib/logo-snapshot.ts). */}
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              Saving a new URL fetches a fresh copy of the logo on the server, shown on the RCD &amp;
              SHAFT tab. If that image differs from the one the SHAFT review was ticked against, the
              review is reset.
            </div>
          </div>
          <div>
            {fieldRow('Telephone Numbers (comma-separated)', 'tns')}
            {/* #115: this is the registrant's free-text declaration; settlement
                delivery reads only the analyst-vetted canonical list on the TNs
                tab. Point analysts there so declared numbers actually ship. */}
            <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
              Declaration only — settlement delivery uses the vetted list on the TNs tab. Copy these over there (a one-click action appears when that list is empty).
            </div>
          </div>
        </div>
      </div>
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Compliance & Trust</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {fieldRow('Engages in telemarketing or auto-dialing', 'useAutodialer', { checkbox: true, checkLabel: 'Yes, engages in telemarketing or auto-dialing' })}
          {fieldRow('Privacy Policy URL', 'privacyUrl')}
          {fieldRow('Consent Process', 'consentProcess', { textarea: true })}
          {fieldRow('Opt-Out Process', 'optOutProcess', { textarea: true })}
          {fieldRow('Consumer Complaint Email', 'complaintEmail')}
          {fieldRow('Consumer Complaint Phone', 'complaintPhone')}
        </div>
      </div>
      {/* #544 — rendered FROM REQUIRED_CERTIFICATIONS, border included. Three
          hand-written rows and a hand-written three-way border condition would
          have been three places to forget the two BCBP warranties, and the
          border is the one an analyst reads first. */}
      <div style={{ ...styles.card, borderLeft: `3px solid ${REQUIRED_CERTIFICATIONS.every(f => d[f]) ? C.success : C.warn}` }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 8 }}>Registrant Certifications</h4>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Required per OA Procedures — Registrant must certify all {REQUIRED_CERTIFICATIONS.length} of the following before submission to Vetting Agent</div>
        {REQUIRED_CERTIFICATIONS.map(f => (
          <div key={f}>{fieldRow(CERTIFICATION_LABELS[f], f, { checkbox: true, checkLabel: CERTIFICATION_STATEMENTS[f] })}</div>
        ))}
      </div>
      <div style={{ ...styles.card, borderLeft: `3px solid ${loaOutstanding(c) ? C.warn : C.border}` }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 8 }}>Letter of Authorization (LOA)</h4>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 12 }}>Required if Registrant is providing information on behalf of a separate BCID Customer. OA will not submit Caller Information to VA until LOA is executed.</div>
        <div style={styles.checkItem}>
          {/* #822 — disabled on a case whose LOA the customer declared (numbers
              382 does not provide, #775): the requirement is theirs, and the API
              refuses an untick with 422. TnsTab disables the same control. */}
          <input id="loa-required" type="checkbox" checked={c.loa?.required || false} onChange={e => {
            updateCase(c.id, {
              loa: { ...c.loa, required: e.target.checked },
              action: { code: 'LOA_REQUIREMENT_SET', detail: { required: e.target.checked } },
            });
          }} disabled={!isEditable || customerDeclaredLoa} />
          <label htmlFor="loa-required" style={{ fontSize: 12, fontWeight: 600 }}>LOA required (Registrant submitting on behalf of separate BCID Customer)</label>
        </div>
        {c.loa?.required && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div>
                <label htmlFor="loa-bcidCustomerName" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>BCID Customer Name</label>
                <input id="loa-bcidCustomerName" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} value={drafts['loa:bcidCustomerName'] ?? ((c.loa as any)?.bcidCustomerName || '')} onChange={e => setDraft('loa:bcidCustomerName', e.target.value)} onBlur={() => {
                  const value = takeDraft('loa:bcidCustomerName');
                  if (value !== undefined && value !== ((c.loa as any)?.bcidCustomerName || '')) updateCase(c.id, { loa: { ...c.loa, bcidCustomerName: value } as any });
                }} readOnly={!isEditable} />
              </div>
              <div>
                <label htmlFor="loa-executionDate" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>LOA Execution Date</label>
                <input id="loa-executionDate" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} type="date" value={(c.loa as any)?.executionDate || ''} onChange={e => updateCase(c.id, { loa: { ...c.loa, executionDate: e.target.value } as any })} readOnly={!isEditable} />
              </div>
              <div>
                <label htmlFor="loa-signerName" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>LOA Signer Name</label>
                <input id="loa-signerName" style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }} value={drafts['loa:signerName'] ?? ((c.loa as any)?.signerName || '')} onChange={e => setDraft('loa:signerName', e.target.value)} onBlur={() => {
                  const value = takeDraft('loa:signerName');
                  if (value !== undefined && value !== ((c.loa as any)?.signerName || '')) updateCase(c.id, { loa: { ...c.loa, signerName: value } as any });
                }} readOnly={!isEditable} />
              </div>
              <div>
                <label htmlFor="loa-status" style={{ fontSize: 11, color: C.textMuted, display: 'block', marginBottom: 3 }}>LOA Status</label>
                <select id="loa-status" style={{ ...styles.select, width: '100%', fontSize: 12, padding: '8px 10px' }} value={(c.loa as any)?.loaStatus || 'PENDING'} onChange={e => {
                  // #834 — on a customer-declared case the status is a note: the
                  // verify names the letter checked, on the TNs tab.
                  updateCase(c.id, {
                    loa: (customerDeclaredLoa
                      ? { ...c.loa, loaStatus: e.target.value }
                      : { ...c.loa, loaStatus: e.target.value, verified: e.target.value === 'EXECUTED' }) as any,
                    action: { code: 'LOA_STATUS_CHANGED', detail: { status: e.target.value } },
                  });
                  if (e.target.value === 'TERMINATED') {
                    updateCase(c.id, { action: { code: 'LOA_TERMINATED', detail: {} } });
                  }
                }} disabled={!isEditable && !canApprove}>
                  <option value="PENDING">Pending</option>
                  <option value="EXECUTED">Executed</option>
                  <option value="TERMINATED">Terminated</option>
                </select>
              </div>
            </div>
            <div style={styles.checkItem}>
              {/* #834 — disabled on a customer-declared case: there the verify
                  names the newest letter the customer sent, which the TNs tab
                  lists, and the API refuses one that names none. */}
              <input id="loa-verified" type="checkbox" checked={c.loa?.verified || false} onChange={e => {
                updateCase(c.id, {
                  loa: { ...c.loa, verified: e.target.checked },
                  action: { code: 'LOA_VERIFIED', detail: { verified: e.target.checked, source: 'onboarding-loa' } },
                });
              }} disabled={customerDeclaredLoa || (!isEditable && !canApprove)} />
              <label htmlFor="loa-verified" style={{ fontSize: 12 }}>LOA verified — signed by BCID Customer, authority and TN assignment confirmed</label>
            </div>
            {customerDeclaredLoa && (
              <div role="note" style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                The customer sent this LOA with their application. Verify it on the TNs tab, which lists the letters
                they sent; the status above does not verify it.
              </div>
            )}
            {(c.loa as any)?.loaStatus === 'TERMINATED' && (
              <div style={{ background: C.dangerLight, borderRadius: 8, padding: 12, marginTop: 8, fontSize: 12, color: C.danger }}>
                ⚠ LOA terminated — updates to BCID Customer's Caller Information are blocked via this Registrant. The BCID Customer can choose to manage their account directly with 382.
              </div>
            )}
          </div>
        )}
      </div>
      {d.callReason && (() => {
        // #541 — this used to be a 15-item array inlined right here, compared
        // case-insensitively, and nobody could say where the 15 came from. It is
        // now the one versioned list in domain/constants.ts, matched exactly:
        // what ships as `default_crn` is the stored bytes, so a value that is
        // not byte-identical to a vetted one IS a custom reason and belongs in
        // front of an analyst.
        const isApproved = isApprovedCallReason(d.callReason);
        return !isApproved ? (
          <div style={{ ...styles.card, borderLeft: `3px solid ${C.warn}` }}>
            <h4 style={{ fontSize: 13, fontWeight: 600, color: C.warn, marginBottom: 8 }}>⚠ Custom Call Reason Detected</h4>
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
              "{d.callReason}" is not on the <a href={BCID_APPROVED_REASONS_URL} target="_blank" rel="noopener noreferrer" style={{ color: C.goldText }}>BCID pre-identified Call Reasons list ↗</a>.
              Custom Call Reasons are permitted but must be flagged for Vetting Agent manual review per OA procedures.
            </div>
            <div style={{ fontSize: 12, color: C.warn, fontWeight: 600 }}>This Call Reason will be flagged for VA manual review upon submission.</div>
          </div>
        ) : null;
      })()}
      <div style={{ ...styles.card }}>
        <h4 style={{ fontSize: 13, fontWeight: 600, color: C.goldText, marginBottom: 12 }}>Additional Notes</h4>
        {fieldRow('Notes / Special Instructions', 'notes', { textarea: true })}
      </div>
    </div>
  );
}

// The sentinel the "Custom…" option carries. Not a possible call reason, and
// never stored — it only ever names which BRANCH of the control is showing.
export const CUSTOM_CALL_REASON_OPTION = '__custom__';

/**
 * Is this value a CUSTOM call reason — that is, present but not byte-identical
 * to a pre-approved one? One function because `CallReasonSelect` asks twice, in
 * two idioms (a mount-time seed and a per-render read), and the two answers have
 * to be the same answer: a seed that disagreed with the render would open the
 * box on a value the select did not show as custom, or vice versa.
 */
function isCustomCallReason(value: string): boolean {
  return value !== '' && !isApprovedCallReason(value);
}

/**
 * The call-reason control, shared by the Onboarding tab and the RCD & SHAFT tab
 * so the two cannot drift (#541).
 *
 * The BCBP requires the Call Reason to be SELECTED from values the VA has
 * registered and vetted, so this is a dropdown over BCID_APPROVED_CALL_REASONS
 * with a "Custom…" escape hatch — custom reasons are permitted, subject to the
 * 35-character cap, and are flagged for manual review downstream.
 *
 * THREE RULES IT IS BUILT ON:
 *
 * 1. NO `maxLength` ON THE CUSTOM INPUT, anywhere, ever. A maxLength drops the
 *    tail of a pasted value without a word, and silent truncation of a
 *    consumer-facing RCD claim is the exact defect #479 item 1 was. Each surface
 *    enforces the cap in its own idiom — a refusal to save here, a submit-gate
 *    error there — and every one of them keeps the over-length text visible.
 *
 * 2. A STORED VALUE IS ALWAYS SHOWN AS IT IS STORED. If it is not byte-identical
 *    to an approved value it opens in the custom box, at whatever length it has.
 *    This is a data-entry change, not a migration: a registration that predates
 *    the list must keep rendering rather than silently reading as blank. That is
 *    also why the match is exact — snapping a near-miss onto the canonical
 *    option would put a value on screen that is not the one that ships.
 *
 * 3. ABSENT STAYS ABSENT. The placeholder is the empty string, which every
 *    consumer already reads as "not set" (the portal's `submit` drops a blank
 *    optional key outright, and the settlement worker's `optionalString` omits
 *    it), so leaving the dropdown alone cannot put an empty `default_crn` on a
 *    DIR. An empty one is worse than a missing one.
 */
export function CallReasonSelect({
  id, value, storedValue, onSelect, onCustomInput, onCustomBlur, readOnly = false,
}: {
  id: string;
  value: string;
  /**
   * What the CASE holds for this claim — never a draft, never a keystroke.
   * Read once, at mount, to decide whether the custom box starts open, and
   * read for nothing else. Separate from `value` because `value` is the
   * DISPLAYED string, which on the Onboarding tab is draft-first: see the
   * seed's own note below for why reading it here was #603 item 1's regression.
   */
  storedValue: string;
  /** An option was chosen — a commit-worthy event on both tabs. */
  onSelect: (next: string) => void;
  /** A keystroke in the custom box. Each tab decides whether that commits. */
  onCustomInput: (next: string) => void;
  onCustomBlur?: () => void;
  readOnly?: boolean;
}) {
  // Whether the analyst has ASKED for the custom box, which is not the same
  // question as whether the current value is custom: switching to "Custom…"
  // clears the value, and an empty custom box must stay open rather than
  // snapping back to the placeholder on the next render.
  //
  // SEEDED from the mounting value (#603) rather than defaulted to false. A
  // stored off-list reason used to open the box through `storedIsCustom` alone,
  // so emptying the input to retype it made `storedIsCustom` false with nothing
  // else holding the box open: the input UNMOUNTED mid-edit and the select
  // snapped to the placeholder. On the Onboarding tab the unmount also took the
  // blur commit with it, because React fires no blur on an unmount — the draft
  // sat at '' while the case still held the old reason, and the two only met
  // again when Submit folded the drafts in. Seeding says "Custom…" is chosen
  // for as long as the analyst is in the box, whatever the box currently holds.
  //
  // A lazy initializer, so it reads the value this control MOUNTED with. Both
  // call sites remount per case (`key={c.id}`), which is what makes mount the
  // right moment to ask: the seed is per case, not once for the life of the tab
  // — a tab that outlives its case would otherwise carry one case's answer onto
  // the next, which is the bug those keys already exist to prevent.
  //
  // AND IT READS `storedValue`, NOT `value`. The two differ on the Onboarding
  // tab, where `value` is `drafts.callReason ?? d.callReason` — draft first —
  // and `drafts` is cleared by an effect keyed on `c.id`. An effect runs AFTER
  // the commit that `key={c.id}` remounted this control in, so for that one
  // render the outgoing case's draft is still in the map: a seed off `value`
  // read the PREVIOUS case's uncommitted keystrokes and latched open. Switching
  // from a case being typed into onto a case whose reason is on the approved
  // list then showed that approved reason sitting inside a custom box, with the
  // dropdown claiming "Custom…". Nothing was written, but the screen said
  // something false about the case on it, which is exactly what the `key`s and
  // the "does not carry an opened custom box across a case change" tests exist
  // to prevent. `storedValue` is the case's own value and cannot carry a draft.
  const [customOpen, setCustomOpen] = useState(() => isCustomCallReason(storedValue));
  const storedIsCustom = isCustomCallReason(value);
  const showCustom = customOpen || storedIsCustom;
  return (
    <>
      <select
        id={id}
        aria-label="Call Reason"
        style={{ ...styles.input, fontSize: 12, padding: '8px 10px' }}
        value={showCustom ? CUSTOM_CALL_REASON_OPTION : value}
        disabled={readOnly}
        onChange={e => {
          const next = e.target.value;
          if (next === CUSTOM_CALL_REASON_OPTION) {
            // Clear on the way in. The box is asking for a NEW reason, and
            // leaving the outgoing approved value sitting in it invites an
            // accidental re-save of a value the analyst just chose to abandon.
            setCustomOpen(true);
            if (value !== '') onSelect('');
            return;
          }
          setCustomOpen(false);
          onSelect(next);
        }}
      >
        <option value="">— Select a call reason —</option>
        {BCID_APPROVED_CALL_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
        <option value={CUSTOM_CALL_REASON_OPTION}>Custom…</option>
      </select>
      {showCustom && (
        <input
          id={`${id}-custom`}
          aria-label="Custom Call Reason"
          placeholder={`Custom reason (max ${CUSTOM_CALL_REASON_MAX} characters)`}
          style={{ ...styles.input, fontSize: 12, padding: '8px 10px', marginTop: 4 }}
          value={value}
          readOnly={readOnly}
          onChange={e => onCustomInput(e.target.value)}
          onBlur={onCustomBlur}
        />
      )}
    </>
  );
}

// #642 — THE ONE READING of who the authorizer is, and of where that reading
// came from. Exported so a test can hold the two halves together, and defined
// at the foot of the file so it adds no lines above the conformance matrix's
// citations into this one (docs/compliance/bcbp-conformance.md, CI-6b).
//
// WHY IT IS NOT `d.authorizerRelationship`. The tab's `d` is stored onboarding
// data first and the filed payload only as a #146 fallback, and the relationship
// was an EDITABLE SELECT until #631: an analyst could set an agent case to
// `employee`, `onboarding.data` kept the edit, and from then on the card said
// "Employee of the caller" directly underneath "Declared by the registrant on
// their application." while the filed application said otherwise — and the
// footnote-3 gate, reading the same stored value, did not fire on a case CTIA's
// fn.3 covers. #631 shut the door; it did not move the cases already through it.
//
// So the FILED value wins wherever an application exists. Stored data is read
// only when `ob.intake` is absent, which is a staff-desk case — one with no
// registrant declaration to be authoritative in the first place. There is
// deliberately no repair write: nothing here edits `onboarding.data`, because
// the registrant's declaration is theirs to correct through a company-profile
// change request, and a silent staff-side rewrite of a stored claim is the
// thing #631 was about.
//
// The relationship and the sentence come back TOGETHER on purpose. They were
// two independent derivations off two different sources, which is precisely how
// they came to contradict each other on screen; one function returning both
// cannot.
export function authorizerRelationshipView(
  intake: Record<string, any> | null | undefined,
  data: Record<string, any>,
): { relationship: string; source: string } {
  const known = (value: unknown): string => {
    const s = String(value);
    return AUTHORIZER_RELATIONSHIPS.includes(s) ? s : 'employee';
  };
  if (!intake) {
    // A STAFF-DESK CASE, and the DECISION about what one may be. There is no
    // filed declaration, so the stored value is the only value — and it is the
    // factory's `employee` unless somebody typed otherwise. Staff-desk cases
    // are employee-authorizer only BY POLICY (decision 2026-09-03, recorded on
    // #642): a registrant using an authorized agent files through the customer
    // portal or the intake API, where the declaration is captured from the
    // registrant and gated on footnote-3 evidence. No staff-side control for
    // it is being built, so the sentence says so rather than leaving an analyst
    // looking for a select that will not appear. The gate above still fires on
    // a stored `authorized_agent` — the policy is what the desk is FOR, not a
    // reason to stop reading a value that is already on a case.
    return {
      relationship: known(data.authorizerRelationship),
      source: 'This case was opened at the staff desk and holds its stored value; no application has been filed. '
        + 'Staff-desk cases are employee-authorizer only by policy (decision 2026-09-03, #642): a registrant whose '
        + 'Authorizer is an authorized agent files through the customer portal or the intake API, where the '
        + 'declaration is captured and gated.',
    };
  }
  if (intake.authorizerRelationship === undefined) {
    // A PRE-#567 APPLICATION, and the one case where stored data wins DESPITE an
    // application existing. There is no filed declaration to be authoritative —
    // the registrant was never asked — so a stored `authorized_agent` here is
    // not the drift this function exists to correct. It is the ONLY record of
    // the relationship, and it is how an agent case WAS recorded: the select was
    // editable from #567 until #631 shut it, which is exactly the window a
    // legacy case would have been corrected in. Returning the literal default
    // instead dropped such a case out of the footnote-3 set, took the evidence
    // card off the screen with the document title, date, signatory and
    // attestation the analyst had already recorded on it, and let the submit
    // through — a false negative on the clause, produced by the change meant to
    // close one.
    //
    // Nothing is stranded by this. A pre-#567 case nobody touched carries the
    // factory's and the mapper's `employee`, which gates nothing, so the
    // ordinary legacy application reads exactly as it did.
    //
    // TWO SENTENCES, chosen on the value that came back. "It reads as the
    // default" is true of an untouched legacy case and false of one an analyst
    // recorded, and the whole point of this line is to say whose statement the
    // value above it is — a sentence that calls a recorded entry a default is
    // the same defect as the label that called a filed agent an employee, one
    // screen further down.
    const stored = known(data.authorizerRelationship);
    return {
      relationship: stored,
      source: stored === 'employee'
        ? 'This application was filed before the relationship was asked, so it reads as the default.'
        : 'This application was filed before the relationship was asked; this is the value recorded on the case.',
    };
  }
  return {
    relationship: known(intake.authorizerRelationship),
    source: 'Declared by the registrant on their application.',
  };
}
